import * as React from "react";
import { connect } from "react-redux";
import { Modal } from "react-bootstrap";
import { isProduction, getConfig } from "config";
import * as _ from "underscore";
import { RouterAction } from "react-router-redux";
import { Button } from "carbon-components-react";
import { Archive16, UserMultiple16, Catalog16, Json32, Notebook16 } from "@carbon/icons-react";

import browserHistory from "web_modules/redux/history";
import { loanStatusToStr } from "web_modules/shared/loan_statuses";
import APIClient from "web_modules/APIClient";
import { getSessionToken, tokenHasRoles } from "web_modules/shared/session";
import { AppShape, MessageShape, DisclosureStage, Role } from "@eave/shared/types/api";
import {
  clearLoanAppContextForMaskingLogs,
  setLoanAppContextForMaskingLogs,
} from "web_modules/utils/sentry";
import AAN from "./AAN";
import { appToBorrowerNames, appToPropertyAddress } from "./shared";

import { mapStateToProps, mapDispatchToProps } from "./selectors";
import StartUnderwritingPrompt from "./shared/StartUnderwritingPrompt";

require("assets/stylesheets/components/underwriter_dashboard/app_editor.scss");

const Pusher = require("pusher-js");

const EMDASH = <span className="hurdles-editor__emdash">—</span>;

export const titleCaseNoSpaces = str =>
  str
    .toLowerCase()
    .split(" ")
    .map(word => word.replace(word[0], word[0].toUpperCase()))
    .join("");

/* AppEditor */

interface AppEditorProps {
  loading: boolean;
  app: AppShape;
  appId: number;
  editingHurdle: string;
  createNewDisclosure: Function;
  loadApp: Function;
  loadMessages: Function;
  loadEvents: Function;
  getUnreadMessageCount: Function;
  messageReceived: Function;
  navigate: (path: string) => RouterAction;
  commentReceived: (message: MessageShape) => void;
  previousLocation: string;
  currentLocation: string;
}

export class AppEditor extends React.Component<AppEditorProps, any> {
  public static disclosureStageTextConverter(disclosureStage: DisclosureStage): string {
    switch (disclosureStage) {
      case DisclosureStage.PreAppLoanEstimate:
        return "Pre-App Loan Estimate";
      case DisclosureStage.LoanEstimate:
        return "Initial Disclosure";
      case DisclosureStage.Redisclosure:
        return "Redisclosure";
      case DisclosureStage.PreliminaryClosingDisclosure:
        return "Preliminary Closing Disclosure";
      case DisclosureStage.ClosingDisclosure:
        return "Closing";
      case DisclosureStage.UnderwritingPackage:
        return "Underwriting Package";
      case DisclosureStage.InvestorReview:
        return "Investor Review";
      default:
        throw new Error(`disclosure stage name of "${disclosureStage}" not handled`);
    }
  }

  constructor(props: AppEditorProps) {
    super(props);

    this.state = {
      showingModal: false,
      signingDateInfo: null,
    };

    this.pusher = new Pusher(getConfig("pusher.key"), {
      encrypted: true,
      cluster: "mt1",
    });

    this.impersonateBorrower = this.impersonateBorrower.bind(this);
  }

  componentDidMount() {
    const { appId, app, loadApp, loadEvents } = this.props;

    this.actionsForNewApp(appId);

    if (app) {
      // loaded by clicking through from UW dashboard
      setLoanAppContextForMaskingLogs(app);
    } else {
      // loaded directly from URL; need to grab app info then refresh
      loadApp(appId);
    }

    // Load Slack and TRUS events
    loadEvents(appId);
  }

  componentWillReceiveProps(nextProps) {
    const { appId } = this.props;

    if (nextProps.appId && nextProps.appId !== appId) {
      this.actionsForNewApp(nextProps.appId);
    }
  }

  componentDidUpdate() {
    const { app } = this.props;

    if (app) {
      setLoanAppContextForMaskingLogs(app);
    }
  }

  componentWillUnmount() {
    clearLoanAppContextForMaskingLogs();
  }

  setupPusher(applicationId) {
    const { commentReceived, messageReceived } = this.props;

    // prefix helps namespace prod in isolation to avoid crossed messages
    const prodPrefix = isProduction() ? "eave_" : "";
    const channelName = `${prodPrefix}application_${applicationId}`;
    if (!this.pusher.channels.channels.hasOwnProperty(channelName)) {
      const channel = this.pusher.subscribe(channelName);

      channel.bind("message", message => {
        if (message.thread_id) {
          commentReceived(message);
        } else {
          messageReceived(applicationId, message);
        }
      });
    }
  }

  renderModal() {
    const { app } = this.props;

    return (
      <Modal
        bsSize="large"
        show
        animation={false}
        onHide={this.toggleState.bind(this, "showingModal")}
      >
        <Modal.Header closeButton>
          <Modal.Title>Loan app ID #{app.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <pre className="raw-app-modal__content">{JSON.stringify(app, null, "\t")}</pre>
          <pre className="raw-app-modal__content">
            {JSON.stringify(this.state.disclosure_entities, null, "\t")}
          </pre>
        </Modal.Body>
      </Modal>
    );
  }

  impersonateBorrower() {
    APIClient.post("/uw/impersonate", {
      reason: "Operator impersonation request",
      application_id: this.props.appId,
    }).then((resp: any) => {
      window.open(resp.link, "_blank");
    });
  }

  toggleState(attr) {
    this.setState(prevState => {
      return { [attr]: !prevState[attr] };
    });
  }

  toggleRestricted(restricted: boolean) {
    const { app, loadApp } = this.props;
    APIClient.post(`/uw/${app.id}/restricted`, { restricted: !restricted }).then(() => {
      loadApp(app.id);
    });
  }

  toggleArchived(is_archived: boolean) {
    const { app, loadApp } = this.props;
    APIClient.post(`/uw/${app.id}/archive`, { archive: !is_archived })
      .then(() => {
        loadApp(app.id);
        if (!is_archived) {
          browserHistory.push("/underwriter");
        }
      })
      .catch(err => {
        if (err?.meta?.error_detail) {
          alert(err.meta.error_detail);
        }
      });
  }

  actionsForNewApp(appId) {
    const { loadMessages, getUnreadMessageCount } = this.props;

    if (appId) {
      loadMessages(appId);
      getUnreadMessageCount(appId);
      this.setupPusher(appId);
    }
  }

  pusher: any;

  render() {
    const { loading, app, loadApp, editingHurdle, navigate } = this.props;
    const { showingModal } = this.state;

    if (
      loading &&
      !(editingHurdle === "DocumentsHurdle" || editingHurdle === "NewDocumentsHurdle")
    ) {
      return <div>Loading…</div>;
    }

    if (!app) {
      return <div>Loan app not found</div>;
    }

    let loanStatus = "—";
    if (app.underwriting_summary && app.underwriting_summary.loan_status) {
      loanStatus = loanStatusToStr(app.underwriting_summary.loan_status);
    }

    return (
      <div className="underwriter-dashboard__children">
        <br />
        <StartUnderwritingPrompt app={app} loadApp={loadApp} />
        <div className="underwriter-dashboard__children-container">
          {showingModal && this.renderModal()}
          <div className="app-editor__header">
            <div className="app-editor__header-info">
              <h3>Loan App ID: {app.external_id || app.lead_number || ""}</h3>
              <p>
                <strong>Borrower(s):</strong> {appToBorrowerNames(app)}
              </p>
              <p>
                <strong>Property:</strong> {appToPropertyAddress(app) || EMDASH}
              </p>
              <p>
                <strong>Loan status:</strong> {loanStatus}
              </p>
            </div>
            <div className="app-editor__header-buttons">
              <Button
                kind="danger--tertiary"
                onClick={this.toggleArchived.bind(this, app.is_archived)}
                renderIcon={Archive16}
              >
                {app.is_archived && "Un-archive"}
                {!app.is_archived && "Archive"}
              </Button>
              <Button kind="tertiary" onClick={this.impersonateBorrower}>
                Impersonate
              </Button>
              <br />
              <Button
                size="sm"
                kind="secondary"
                onClick={this.toggleState.bind(this, "showingModal")}
                renderIcon={Json32}
              >
                View JSON
              </Button>
              <Button
                size="sm"
                kind="secondary"
                onClick={() => navigate(`/underwriter/app/${app.id}/notes`)}
              >
                <div className="app-editor__show-notes">Notes & Events</div>
              </Button>
              <Button
                size="sm"
                kind="secondary"
                onClick={() => {
                  APIClient.post(`/uw/loan_applications/${app.id}/external_id`, {})
                    .then(() => {
                      window.location.reload();
                    })
                    .catch(e => {
                      console.warn(e);
                      alert("There was an error generating the external id. Please contact Eng.");
                      throw e;
                    });
                }}
                disabled={app.external_id !== null}
              >
                Generate Loan #
              </Button>
              {tokenHasRoles(getSessionToken(), Role.CanViewRestricted) && (
                <Button bsSize="small" onClick={this.toggleRestricted.bind(this, app.restricted)}>
                  {app.restricted && "Un-restrict file"}
                  {!app.restricted && "Restrict file access"}
                </Button>
              )}
            </div>
          </div>
          <AAN app={app} />
        </div>
      </div>
    );
  }
}

export default connect<any, any, any>(mapStateToProps, mapDispatchToProps)(AppEditor);
